<template>
  <div class="tag-anchor" :style="styles" :id="$attrs._id">
    <div class="tag-line" :style="lineStyle"></div>

    <div
      v-if="tailPosition !== 'none'"
      class="tag-dot"
      :style="{ backgroundColor: tailColourEffective }"
    ></div>

    <div
      class="tag-action"
      :class="[
        `tag-action--${type}`,
        { 'tag-action--box-shadow': boxShadow !== false }
      ]"
      ref="el"
      :style="labelStyle"
    >
      <fa :icon="icon" />
    </div>
  </div>
</template>

<script>
export default {
  props: [
    "background-colour",
    "pos",
    "label",
    "type",
    "tail-height",
    "tail-position",
    "tail-colour",
    "icon",
    "box-shadow",
    "tag-scale"
  ],

  computed: {
    styles() {
      return {
        left: `${this.pos.x / 20}em`,
        top: `${this.pos.y / 20}em`
      };
    },

    tailColourEffective() {
      return this.tailColour || this.backgroundColour;
    },

    scaleEffective() {
      const raw = Number(this.tagScale);
      const s = Number.isFinite(raw) ? raw : 1;
      return Math.min(2, Math.max(0.5, s));
    },

    // find transform origin for tag size sclaing from tail position
    actionTransformOrigin() {
      switch (this.tailPosition) {
        case "bottom-left":
          return "0% 100%";
        case "bottom-center":
          return "50% 100%";
        case "bottom-right":
          return "100% 100%";

        case "top-left":
          return "0% 0%";
        case "top-center":
          return "50% 0%";
        case "top-right":
          return "100% 0%";

        case "left-center":
          return "0% 50%";
        case "right-center":
          return "100% 50%";

        case "none":
        default:
          return "50% 50%";
      }
    },

    labelStyle() {
      let tailStyles = {};

      switch (this.tailPosition) {
        case "none":
          tailStyles = {
            transform: `translate(-50%, -50%)`
          };
          break;

        case "bottom-left":
          tailStyles = {
            bottom: "0",
            transform: `translate(-1em, -${this.tailHeight * 1.33}em)`
          };
          break;

        case "bottom-center":
          tailStyles = {
            bottom: "0",
            left: "50%",
            transform: `translate(-50%, -${this.tailHeight * 1.33}em)`
          };
          break;

        case "bottom-right":
          tailStyles = {
            bottom: "0",
            right: "0",
            transform: `translate(1em, -${this.tailHeight * 1.33}em)`
          };
          break;

        case "top-left":
          tailStyles = {
            transform: `translate(-1em, ${this.tailHeight * 1.33}em)`
          };
          break;

        case "top-center":
          tailStyles = {
            left: "50%",
            transform: `translate(-50%, ${this.tailHeight * 1.33}em)`
          };
          break;

        case "top-right":
          tailStyles = {
            right: "0",
            transform: `translate(1em, ${this.tailHeight * 1.33}em)`
          };
          break;

        case "left-center":
          tailStyles = {
            left: "0",
            top: ".125em",
            transform: `translate(${this.tailHeight * 1.33}em, -50%)`
          };
          break;

        case "right-center":
          tailStyles = {
            right: "0",
            top: ".125em",
            transform: `translate(-${this.tailHeight * 1.33}em, -50%)`
          };
          break;

        default:
          return "";
      }

      //  SCALE ONLY THE ACTION, FROM TAIL CONNECTION POINT
      tailStyles.transform = `${tailStyles.transform} scale(${this.scaleEffective})`;
      tailStyles.transformOrigin = this.actionTransformOrigin;

      let colorStyles = {};
      if (this.backgroundColour) {
        colorStyles = {
          backgroundColor: this.backgroundColour
        };
      }

      return Object.assign(colorStyles, tailStyles);
    },

    lineStyle() {
      if (this.tailPosition === "none") return { display: "none" };

      const style = {
        backgroundColor: this.tailColourEffective,
        zIndex: 6
      };

      switch (this.tailPosition) {
        case "bottom-left":
        case "bottom-center":
        case "bottom-right":
          return {
            ...style,
            height: `${this.tailHeight}em`,
            transform: `translateY(-${this.tailHeight}em)`
          };

        case "top-left":
        case "top-center":
        case "top-right":
          return {
            ...style,
            bottom: 0,
            height: `${this.tailHeight}em`,
            transform: `translateY(${this.tailHeight}em)`
          };

        case "left-center":
          return {
            ...style,
            height: ".25em",
            right: 0,
            top: "-.125em",
            width: `${this.tailHeight}em`,
            transform: "translateX(0)"
          };

        case "right-center":
          return {
            ...style,
            height: ".25em",
            left: 0,
            top: "-.125em",
            width: `${this.tailHeight}em`,
            transform: `translateX(-${this.tailHeight}em)`
          };

        default:
          return style;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.tag-anchor {
  color: $action-tag-bg-color;
  font-size: 1em;
  position: absolute;
  cursor: pointer;
  pointer-events: auto;
  transition: opacity 200ms ease-in-out, box-shadow 200ms ease-in-out;
  z-index: 25;

  &.inactive {
    opacity: 0.5 !important;
  }
}

.tag-dot {
  border-radius: 50%;
  bottom: -0.25em;
  height: 0.5em;
  left: -0.25em;
  pointer-events: auto;
  position: absolute;
  width: 0.5em;
}

.tag-line {
  left: -0.125em;
  pointer-events: auto;
  position: absolute;
  width: 0.25em;
}

.tag-action {
  align-items: center;
  background-color: $action-tag-bg-color;

  /* match touchscreen */
  border: 2px solid #fff;
  border-radius: $action-tag-border-radius;

  box-shadow: none;
  color: $action-tag-font-color;
  display: flex;
  font-family: $touchscreen-font;
  font-size: $action-tag-font-size;
  font-weight: 400;

  /* match touchscreen size */
  height: 4.5em;
  width: 4.5em;
  padding: 1em;

  justify-content: center;
  letter-spacing: 0.1em;
  line-height: 1.1;
  pointer-events: auto;
  position: absolute;
  text-align: center;
  text-transform: uppercase;
  user-select: none;

  &--box-shadow {
    box-shadow: $tag-box-shadow;
  }

  /* match touchscreen icon sizing behaviour */
  svg {
    height: 100%;
    width: 100%;
    font-size: unset; /* prevents the 3.8em behaviour */
  }
}
</style>
